import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Group, Stack, Text } from "@mantine/core";
import { useNavigate } from "react-router-dom";

type BarcodeScannerProps = {
  onDetected: (code: string) => void;
  onCancel?: () => void;
};

export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  onDetected,
  onCancel,
}) => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const detectorRef = useRef<any>(null);
  const lastInvalidRef = useRef<number>(0);

  const [error, setError] = useState<string | null>(null);
  const [supported, setSupported] = useState<boolean>(true);

  /**
   * ✅ 스트림/루프/비디오 상태까지 "완전" 정리
   * - play() pending 중 언마운트/재실행이 와도 안전하게
   */
  const stopStream = useCallback(() => {
    // detect loop 중단
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    // video 정리 (중요)
    const video = videoRef.current;
    if (video) {
      try {
        video.pause();
      } catch {
        // ignore
      }
      try {
        // srcObject 제거가 핵심 (새 load/정리 레이스 완화)
        (video as any).srcObject = null;
      } catch {
        // ignore
      }
    }

    // stream track stop
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  /**
   * ✅ RAF 기반 바코드 탐지 루프
   */
  const loopDetect = useCallback(() => {
    const video = videoRef.current;
    const detector = detectorRef.current;
    if (!video || !detector) return;

    const tick = async () => {
      try {
        // HAVE_CURRENT_DATA(2) 이상이면 현재 프레임 사용 가능
        if (video.readyState >= 2) {
          const barcodes = await detector.detect(video);

          if (barcodes && barcodes.length > 0) {
            const code =
              barcodes[0]?.rawValue || barcodes[0]?.displayValue || "";

            if (code) {
              const codeStr = String(code).trim();

              // 내비게이션 가능 여부 판정
              let isNavigable = false;
              const isNumeric = /^\d+$/.test(codeStr);
              try {
                // If it parses as URL, it's navigable
                new URL(codeStr);
                isNavigable = true;
              } catch {
                if (codeStr.startsWith("/")) isNavigable = true;
                if (isNumeric) isNavigable = true;
              }

              if (!isNavigable) {
                // 숫자 등 URL이 아닌 값: 이동하지 않고 메시지 표시 후 계속 스캔
                const now = Date.now();
                if (now - lastInvalidRef.current > 1200) {
                  setError("QR 코드에 URL이 포함되어 있지 않습니다.");
                  lastInvalidRef.current = now;
                }
                // 계속 루프
              } else {
                // 유효: 스트림 중지하고 콜백 및 내비게이션 처리
                stopStream();

                // 우선 콜백 실행 (모달 종료 등 외부 처리 우선)
                try {
                  onDetected(codeStr);
                } catch {
                  // ignore
                }

                // 모달이 닫힐 여지를 준 뒤 라우팅 처리
                setTimeout(() => {
                  try {
                    // 0) 숫자만인 경우: 현재 오리진 기준 /work/{id}
                    if (isNumeric) {
                      navigate(`/work/${codeStr}`);
                      return;
                    }
                    // 1) 절대 URL인 경우
                    try {
                      const url = new URL(codeStr);
                      // 동일 오리진이면 SPA 내 라우팅, 아니면 전체 페이지 이동
                      if (url.origin === window.location.origin) {
                        navigate(`${url.pathname}${url.search}${url.hash}`);
                      } else {
                        window.location.href = url.toString();
                      }
                      return;
                    } catch {
                      // new URL 실패 시 경로로 판단
                    }

                    // 2) 슬래시로 시작하는 내부 경로
                    if (codeStr.startsWith("/")) {
                      navigate(codeStr);
                      return;
                    }
                  } catch {
                    // ignore navigation errors
                  }
                }, 0);
                return;
              }
            }
          }
        }
      } catch {
        // 탐지 실패는 무시하고 루프 지속
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [onDetected, stopStream, navigate]);

  useEffect(() => {
    let cancelled = false;

    const setup = async () => {
      try {
        setError(null);

        // ✅ BarcodeDetector 지원 확인
        const hasDetector =
          typeof (window as any).BarcodeDetector !== "undefined";

        if (!hasDetector) {
          setSupported(false);
          setError(
            "이 브라우저는 카메라 바코드 스캔(BarcodeDetector)을 지원하지 않아요. 최신 Chrome/Edge를 사용해 주세요.",
          );
          return;
        }

        setSupported(true);

        // ✅ Detector 생성
        const BarcodeDetectorCtor: any = (window as any).BarcodeDetector;
        const formats = await BarcodeDetectorCtor.getSupportedFormats?.();

        detectorRef.current = new BarcodeDetectorCtor({
          formats: formats?.length
            ? formats
            : ["code_128", "ean_13", "ean_8", "qr_code", "upc_a", "upc_e"],
        });

        // ✅ 카메라 스트림 시작 (후면 카메라 선호)
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });

        // setup 도중 언마운트/닫기 되었으면 즉시 정리
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;

        const video = videoRef.current;
        if (video) {
          // 기존 재생/소스 정리 후 연결
          try {
            video.pause();
          } catch {}

          (video as any).srcObject = stream;

          // ✅ loadedmetadata 이후 play (안정성↑)
          await new Promise<void>((res) => {
            const onLoaded = () => {
              video.removeEventListener("loadedmetadata", onLoaded);
              res();
            };
            video.addEventListener("loadedmetadata", onLoaded);
          });

          if (cancelled) return;

          // ✅ play()는 race로 reject될 수 있으니 조용히 처리
          await video.play().catch(() => {});
        }

        if (!cancelled) loopDetect();
      } catch (e: any) {
        if (!cancelled) {
          setError(
            e?.message ||
              "카메라 초기화 중 오류가 발생했어요. 권한을 확인해 주세요.",
          );
        }
      }
    };

    setup();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [loopDetect, stopStream]);

  return (
    <Stack spacing="xs" align="center" style={{ minWidth: 440 }}>
      <video
        ref={videoRef}
        playsInline
        muted
        style={{
          width: 320,
          height: 240,
          background: "#000",
          borderRadius: 8,
        }}
      />

      {error && (
        <Text size="sm" c="red.5" ta="center">
          {error}
        </Text>
      )}

      {!error && !supported && (
        <Text size="sm" ta="center">
          브라우저 미지원으로 스캔을 사용할 수 없어요.
        </Text>
      )}

      <Group position="center" w="100%">
        <Button
          variant="default"
          onClick={() => {
            stopStream(); // ✅ 닫기 누를 때도 즉시 정리
            onCancel?.();
          }}
        >
          닫기
        </Button>
      </Group>
    </Stack>
  );
};

export default BarcodeScanner;
